import { createContext, useContext, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * Ant Design `Row` / `Col` replacement — a 24-column responsive grid with `gutter`
 * and `span`/`xs`/`sm`/`md`/`lg`/`xl`/`xxl` breakpoints (JS-resolved so responsive
 * spans keep working). Lets us drop `import { Row, Col } from 'antd'`.
 */
const BPS: [BpKey, number][] = [['xs', 0], ['sm', 576], ['md', 768], ['lg', 992], ['xl', 1200], ['xxl', 1600]];
type BpKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

function useBpIndex() {
  const [i, setI] = useState(() => resolve(typeof window !== 'undefined' ? window.innerWidth : 1280));
  useEffect(() => {
    const on = () => setI(resolve(window.innerWidth));
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return i;
}
function resolve(w: number): number {
  let idx = 0;
  BPS.forEach(([, min], i) => { if (w >= min) idx = i; });
  return idx;
}

const RowCtx = createContext<[number, number]>([0, 0]); // [horizontal, vertical] gutter px

function normGutter(g?: number | [number, number]): [number, number] {
  if (g == null) return [0, 0];
  return Array.isArray(g) ? [g[0] ?? 0, g[1] ?? 0] : [g, 0];
}

export interface RowProps {
  gutter?: number | [number, number];
  justify?: 'start' | 'end' | 'center' | 'space-between' | 'space-around';
  /** antd parity, including `stretch` — every Col in a line gets the height of the tallest one.
   *  Needed by card rows (KPI tiles): with the default `top`, a tile whose content is one line
   *  shorter than its neighbours' renders a shorter card and the row's bottom edge goes ragged. */
  align?: 'top' | 'middle' | 'bottom' | 'stretch';
  wrap?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export function Row({ gutter, justify = 'start', align = 'top', wrap = true, className, style, children }: RowProps) {
  const [h, v] = normGutter(gutter);
  const j = { start: 'justify-start', end: 'justify-end', center: 'justify-center', 'space-between': 'justify-between', 'space-around': 'justify-around' }[justify];
  const a = { top: 'items-start', middle: 'items-center', bottom: 'items-end', stretch: 'items-stretch' }[align];
  return (
    <RowCtx.Provider value={[h, v]}>
      <div className={cn('flex', wrap && 'flex-wrap', j, a, className)} style={{ marginLeft: -h / 2, marginRight: -h / 2, rowGap: v, ...style }}>
        {children}
      </div>
    </RowCtx.Provider>
  );
}

export interface ColProps {
  span?: number; offset?: number;
  xs?: number; sm?: number; md?: number; lg?: number; xl?: number; xxl?: number;
  flex?: string; className?: string; style?: CSSProperties; children?: ReactNode;
  onClick?: () => void;
}

export function Col(props: ColProps) {
  const [h] = useContext(RowCtx);
  const bpIdx = useBpIndex();
  // pick the largest defined breakpoint span at or below the current width; else span; else 24
  let span = props.span;
  for (let i = bpIdx; i >= 0; i--) {
    const key = BPS[i][0];
    const val = props[key];
    if (typeof val === 'number') { span = val; break; }
  }
  if (span == null) span = 24;
  const pct = (span / 24) * 100;
  const offsetPct = props.offset ? (props.offset / 24) * 100 : 0;
  const style: CSSProperties = props.flex
    // `maxWidth: 100%` mirrors the span branch below: a Col must never be wider than the Row it sits
    // in. Without it a `flex="none"` column (flex: 0 0 auto) sizes to its content's MAX-content — so
    // an action group stayed one long line and hung off the page even after the row was free to wrap.
    ? { flex: props.flex, maxWidth: '100%', paddingLeft: h / 2, paddingRight: h / 2, ...props.style }
    : { flex: `0 0 ${pct}%`, maxWidth: `${pct}%`, marginLeft: offsetPct ? `${offsetPct}%` : undefined, paddingLeft: h / 2, paddingRight: h / 2, ...props.style };
  return <div className={props.className} style={style} onClick={props.onClick}>{props.children}</div>;
}
